import { FeatureDc } from "@evergis/api";
import React, { FC, useCallback, useMemo } from "react";
import { IconToggle, IconTypesKeys, IOption, Menu, Popup, useToggle } from "@evergis/ui";
import { useTranslation } from "react-i18next";
import { GeometryEditorMode, geometryModeSet } from "../../../../../ducks/geometryEditor/geometryEditor";
import { isGroupedFeature } from "../../../../../evergis/core/feature/isGroupedFeature";
import { useActionWithArgs } from "../../../../../hooks/useAction";

import { useConfirmDialog } from "../../../../../hooks/useDialogs";
import { useUngroupFeature } from "../../../hooks/useUngroupFeature";
import { ControlsProps } from "../types";
import { useFeatureEditCoordinates } from "./useFeatureEditCoordinates";

export enum EditGeometryAction {
  Ungroup = "Ungroup",
  Group = "Group",
  Coordinates = "Coordinates",
}

export const EditFeatureGeometryActions: FC<ControlsProps> = ({ feature }) => {
  const { t } = useTranslation("featureCard");
  const [isMenuOpen, toggleMenu] = useToggle(false);
  const isGrouped = isGroupedFeature(feature as FeatureDc);

  const geometryActions = useMemo(
    (): IOption[] =>
      [
        isGrouped && {
          text: t("ungroup"),
          value: EditGeometryAction.Ungroup,
          icon: "ungroup_features" as IconTypesKeys,
        },
        {
          text: t("addToGroup"),
          value: EditGeometryAction.Group,
          icon: "group_features" as IconTypesKeys,
        },
        {
          text: t("coordinates"),
          value: EditGeometryAction.Coordinates,
          icon: "xy" as IconTypesKeys,
        },
      ].filter(Boolean),
    [isGrouped, t],
  );

  const showConfirmUngroup = useConfirmDialog();
  const [ungroupFeature] = useUngroupFeature(feature);

  const handleClickUngroupButton = useCallback(() => {
    showConfirmUngroup([""], {
      content: t("ungroupConfirm"),
      onConfirm: ungroupFeature,
      okText: t("ungroup"),
      cancelText: t("actions.cancel", { ns: "common" }),
      options: {
        maxWidth: "40rem",
      },
    });
  }, [showConfirmUngroup, t, ungroupFeature]);

  const setGeometryExpandMode = useActionWithArgs(geometryModeSet, GeometryEditorMode.Expand);
  const showEditCoordinatesDialog = useFeatureEditCoordinates();

  const selectOption = useCallback(
    ([{ value }]: IOption[]) => {
      switch (value as EditGeometryAction) {
        case EditGeometryAction.Ungroup:
          handleClickUngroupButton();
          break;
        case EditGeometryAction.Group:
          setGeometryExpandMode();
          break;
        default:
          showEditCoordinatesDialog();
          break;
      }

      toggleMenu();
    },
    [handleClickUngroupButton, setGeometryExpandMode, showEditCoordinatesDialog, toggleMenu],
  );

  return (
    <Popup
      placement={"right"}
      isVisible={isMenuOpen}
      onRequestClose={toggleMenu}
      anchor={ref => (
        <IconToggle
          innerRef={ref}
          kind="coordinates_actions"
          isSelected={isMenuOpen}
          title={t("coordinatesActions")}
          onClick={toggleMenu}
          accent
        />
      )}
    >
      <Menu options={geometryActions} selectOption={selectOption} />
    </Popup>
  );
};
